import { IconSelector, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import { useEffect, useRef, useState } from "react";
import { type ClearIndicatorProps, type DropdownIndicatorProps, type OptionProps, type SingleValue, components } from "react-select";
import AsyncSelect from "react-select/async"; // Import AsyncSelect from 'react-select/async'

type ComboBoxOption = {
	value?: string | number | null;
	label?: string | number | null;
};

type ComboboxProps<T> = {
	placeholder?: string;
	className?: string;
	debounceMs?: number;
	disabled?: boolean;
	placeholderValue?: ComboBoxOption;
	clearable?: boolean;
	loadOptions: (inputValue: string) => Promise<T[]>;
	minHeight?: number;
	maxHeight?: number;
	defaultOptions?: boolean;
	onValueChange?: (value: ComboBoxOption | ComboBoxOption["value"]) => void;
	returnFullOption?: boolean;
	defaultValue?: ComboBoxOption["value"];
	itemComponent?: React.ComponentType<OptionProps<ComboBoxOption, false>>;
};

const ClearIndicator = (props: ClearIndicatorProps<ComboBoxOption, false>) => (
	<components.ClearIndicator {...props}>
		<IconX size={18} stroke={1.5} />
	</components.ClearIndicator>
);

const DropdownIndicator = (props: DropdownIndicatorProps<ComboBoxOption, false>) => (
	<components.DropdownIndicator {...props}>
		<IconSelector size={18} stroke={1.5} />
	</components.DropdownIndicator>
);

const Option = (props: OptionProps<ComboBoxOption, false>) => <components.Option {...props} />;

export default function Combobox<T>({
	placeholder,
	debounceMs = 500,
	clearable = true,
	minHeight = 300,
	maxHeight = 400,
	defaultOptions = true,
	returnFullOption = false,
	...props
}: ComboboxProps<T>) {
	const initialKeyRef = useRef(`${props.defaultValue}-${props.placeholderValue?.value}`);
	const [selectedOption, setSelectedOption] = useState<ComboBoxOption | null>(
		props.placeholderValue || (props.defaultValue ? { value: props.defaultValue, label: undefined } : null),
	);
	const isUserSelection = useRef(false);
	const loadOptionsDebounced = debounce(props.loadOptions, debounceMs);

	const comboBoxOptions = {
		defaultOptions,
		isClearable: clearable,
		isDisabled: props.disabled,
		dropdownIndicator: "yes",
		menuPlacement: "auto",
		minMenuHeight: minHeight,
		maxMenuHeight: maxHeight,
		onChange: (option: SingleValue<ComboBoxOption>) => {
			isUserSelection.current = true;
			setSelectedOption(option);

			if (returnFullOption) {
				props.onValueChange(option);
			} else {
				props.onValueChange(option?.value || null);
			}
		},
	};

	useEffect(() => {
		if (isUserSelection.current) {
			isUserSelection.current = false;
			return;
		}
		if (props.placeholderValue) {
			setSelectedOption(props.placeholderValue);
		} else if (props.defaultValue) {
			setSelectedOption({ value: props.defaultValue, label: undefined });
		} else {
			setSelectedOption(null);
		}
	}, [props.defaultValue, props.placeholderValue]);

	const handleLoadOptions = debounce((inputValue: string, callback: (options: T[]) => void) => {
		debounce(() => {
			if (props.loadOptions) {
				props
					.loadOptions(inputValue)
					.then((options) => {
						callback(options);
					})
					.catch((error) => {
						console.error(error);
						callback([]);
					});
			} else {
				callback([]);
			}
		}, debounceMs)();
	}, 500);

	return (
		<AsyncSelect
			key={initialKeyRef.current}
			components={{ ClearIndicator, DropdownIndicator, Option }}
			placeholder={placeholder}
			loadOptions={handleLoadOptions}
			value={selectedOption}
			className={clsx("w-full text-base", props.className)}
			classNames={{
				control: ({ isDisabled }) =>
					clsx(
						"flex flex-row w-full min-h-[38px] max-h-[38px] h-[38px] pl-3 pr-0 gap-3 text-base !rounded-md",
						isDisabled
							? "!bg-muted !border-border !text-muted-foreground !cursor-not-allowed"
							: "!bg-background !border-border focus-within:!border-focus-border focus-within:!ring-2 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
					),
				valueContainer: () => "!p-0",
				placeholder: () => "!text-muted-foreground",
				singleValue: ({ isDisabled }) => clsx(isDisabled ? "!text-muted-foreground" : "!text-foreground"),
				menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
				option: ({ isFocused, isSelected }) =>
					clsx(
						"!px-3 !py-2 !text-base !text-foreground !cursor-pointer",
						isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
					),
				dropdownIndicator: () => "!text-muted-foreground",
				clearIndicator: () => "!text-muted-foreground !cursor-pointer",
			}}
			{...comboBoxOptions}
		/>
	);
}
